import { generatePath } from '@/services/router/generate-path';
import { isApiTriggeredBroadcast } from './utils';

/**
 * Where a campaign's pages live. An API-triggered broadcast sits under the
 * broadcasts list and every other campaign under the automations list, so
 * each builder branches on the campaign's family.
 *
 * Where this app has its own route for a page (the workflow canvas, the
 * broadcasts list, the composer), the builder emits that route. Each of those
 * routes forwards to the embedded app's address while its flag is off, so a
 * link built here works for every account.
 */

type WorkspaceIdInput = string | number;

/** The campaign facts a link depends on. */
type HrefCampaign = { id: string; type?: string | null };

/** The campaign facts a workflow link depends on. */
type WorkflowHrefCampaign = HrefCampaign & { state?: string | null };

/** The list a campaign belongs to. */
export function campaignListHref(workspaceId: WorkspaceIdInput, campaign: Pick<HrefCampaign, 'type'>): string {
  return isApiTriggeredBroadcast(campaign)
    ? generatePath('/workspaces/:workspaceId/broadcasts', { workspaceId })
    : generatePath('/workspaces/:workspaceId/journeys/automations', { workspaceId });
}

/**
 * A campaign's overview. A campaign still in its setup flow has no overview,
 * and the overview sends it on to its setup.
 */
export function campaignOverviewHref(workspaceId: WorkspaceIdInput, campaign: HrefCampaign): string {
  return isApiTriggeredBroadcast(campaign)
    ? generatePath('/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview', {
        workspaceId,
        broadcastId: campaign.id,
      })
    : generatePath('/workspaces/:workspaceId/journeys/automations/:campaignId/overview', {
        workspaceId,
        campaignId: campaign.id,
      });
}

type CampaignWorkflowHrefOptions = {
  /** The step to open in the workflow, or none to open it at the start. */
  actionId?: string;
};

/**
 * A campaign's workflow, opened at one step when `actionId` is given.
 *
 * An automation opens on the workflow builder's own address, which serves it
 * in any state. An API-triggered broadcast keeps its own. Its plain workflow
 * address sends a draft to its setup and any other broadcast to its overview,
 * but drops the query on the way, so a link to one step goes straight to the
 * setup or overview workflow, picked by the broadcast's state.
 */
export function campaignWorkflowHref(
  workspaceId: WorkspaceIdInput,
  campaign: WorkflowHrefCampaign,
  { actionId }: CampaignWorkflowHrefOptions = {},
): string {
  if (!isApiTriggeredBroadcast(campaign)) {
    return generatePath(
      '/workspaces/:workspaceId/automations/:campaignId/workflow',
      { workspaceId, campaignId: campaign.id },
      { actionId },
    );
  }
  const params = { workspaceId, broadcastId: campaign.id };
  if (actionId == null) {
    return generatePath('/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/workflow', params);
  }
  return campaign.state === 'draft'
    ? generatePath(
        '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/setup/workflow/actions',
        params,
        { actionId },
      )
    : generatePath(
        '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview/workflow/actions',
        params,
        { actionId },
      );
}

type ComposerActionHrefOptions = {
  /** Where the composer was opened from, for its analytics. */
  initiationType?: string;
  /** A starting point the composer fills the content in with. */
  preset?: string;
};

/**
 * The composer for one action's content, at its `journeys` address.
 *
 * Both composer routes read the action afresh before deciding who renders it.
 * The `journeys` route forwards only an action this app has a composer for to
 * the composer's own address. The composer's own address hands every other
 * action back to the `journeys` one, which then reads it a second time. While
 * this app has a composer for few action types, most links would pay that
 * second read and loader, or a redirect with the flag off, so links point at
 * the `journeys` address until most types have a composer here.
 */
export function composerActionHref(
  workspaceId: WorkspaceIdInput,
  actionId: string,
  { initiationType, preset }: ComposerActionHrefOptions = {},
): string {
  return generatePath(
    '/workspaces/:workspaceId/journeys/composer/actions/:actionId',
    { workspaceId, actionId },
    { initiationType, preset },
  );
}

/**
 * The automations spelling of a path built from a `/journeys/campaigns` route.
 * Automations are served under `/journeys/automations`, and the `campaigns`
 * spelling only redirects there, so links and comparisons use this one.
 */
export function toAutomationsPath(path: string): string {
  return path.replace(/\/journeys\/campaigns(?=\/|$)/, '/journeys/automations');
}
